import PropTypes from 'prop-types';
import { createContext, useEffect, useRef, useState } from 'react';
import { io } from "socket.io-client";
import fixWebmDuration from "fix-webm-duration";

// project import
import useAuth from 'hooks/useAuth';
import { AXIOS_BASE_URL } from 'config';
import { UPDATE_PUNCTUATIONS, UPDATE_TEXT_TO_NUMBERS } from 'store/actions';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { fileUpdater } from 'store/slices/files';
import { useMediaQuery, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { setOnlineUsers } from 'store/slices/onlineUsers';
import { setReportData } from 'store/slices/monitoring';

// initial state
const initialState = {
    connected: false,
    serviceSocketReady: false,
    recording: false,
    lastPartial: '',
    lastResult: '',
    record: () => { },
    resetForm: () => { },
    emit: () => { },
    clearResalt: () => { }
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const SocketContext = createContext(initialState);

export const SocketProvider = ({ children }) => {
    const [state, setState] = useState(initialState);
    const { isLoggedIn, updateSettings, logout } = useAuth();

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const dispatch = useDispatch();
    const socket = useRef(null)
    const stream = useRef(null)
    const recorder = useRef(null)
    const chunks = useRef([])
    const voiceDuration = useRef(null)
    const location = useLocation();

    const monitorInterval = useRef(null)

    useEffect(() => {
        if (isLoggedIn) {
            const token = localStorage.getItem("serviceToken");

            if (process.env.NODE_ENV === "development") {
                socket.current = io(AXIOS_BASE_URL, { extraHeaders: { token: token, platform: isMobile ? "mobile-app" : "browser" } })
                socket.current.connect();
            } else {
                socket.current = io("", { extraHeaders: { token: token, platform: isMobile ? "mobile-app" : "browser" } })
                socket.current.connect();
            }


            socket.current.on("connect", () => {
                setState(prv => ({ ...prv, connected: true }))
                console.log("IO Connected");
            })

            socket.current.on("disconnect", () => {
                setState(prv => ({
                    ...prv,
                    connected: false,
                    serviceSocketReady: false,
                    recording: false,
                    lastPartial: '',
                    lastResult: ''
                }))
                console.log("IO Disconnected");
            })

            socket.current.on("service-socket-ready", ({ serviceSocketReady }) => {
                setState(prv => ({ ...prv, serviceSocketReady }))
                console.log(`Asr Service ${serviceSocketReady ? "ready" : "Not Ready"}`);
            })

            socket.current.on("result", (res) => {
                if ('partial' in res) {
                    if (res.partial.length) {
                        setState(prv => ({ ...prv, lastPartial: res.partial }))
                    }
                }
                else if ('result' in res) {
                    setState(prv => ({ ...prv, lastPartial: '', lastResult: res.text }))
                }
            })

            socket.current.on("punctuations", (punctuationsStatus) => {
                updateSettings(UPDATE_PUNCTUATIONS, { punctuations: punctuationsStatus })
            })

            socket.current.on("text-to-number", (textToNumberStatus) => {
                updateSettings(UPDATE_TEXT_TO_NUMBERS, { textToNumber: textToNumberStatus })
            })

            socket.current.on("queue-report", (report) => {
                const { id, code, type } = report
                if (code && type) {
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: code.toString(),
                            variant: 'alert',
                            alert: {
                                color: type
                            },
                            close: true
                        })
                    )
                }
                if (id) {
                    dispatch(fileUpdater(report))
                }
            })
            socket.current.on("online-users", (users) => {
                dispatch(setOnlineUsers(users))
            })
            socket.current.on("system-monitor", (report) => {
                dispatch(setReportData(report))
            })
            socket.current.on("message", async ({ code, statusCode }) => {
                if (statusCode == 401) {
                    await logout()
                }

                dispatch(
                    openSnackbar({
                        open: true,
                        message: code.toString(),
                        variant: 'alert',
                        alert: {
                            color: "error"
                        },
                        close: true
                    })
                )
                setState((prv) => ({
                    ...prv,
                    recording: false,
                }));
                recorder.current?.stop();
                // console.log(err);
                // socket.current.emit('stop-microphone');

            })
        }
        else {
            socket.current?.disconnect();
        }
    }, [isLoggedIn])


    useEffect(() => {
        if (location.pathname !== "/microphone-recognition" && state.recording) {
            resetForm()
        }
    }, [location])

    useEffect(() => {
        if (!state.serviceSocketReady && state.recording) {
            setState((prv) => ({
                ...prv,
                recording: false,
            }));
            stream.current?.getTracks().forEach((track) => {
                track.stop();
            });
            recorder.current?.stop();
            socket.current?.emit('stop-microphone');
        }
    }, [state.serviceSocketReady])

    useEffect(() => {
        if (!state.connected) {
            stream.current?.getTracks().forEach((track) => {
                track.stop();
            });
            if (recorder.current?.state == "recording") {
                recorder.current?.stop();
            }
        }
    }, [state.connected])

    useEffect(() => {
        if (location.pathname === "/dashboard/server-monitoring") {
            monitorInterval.current = setInterval(() => {
                socket.current?.emit("get-system-monitor")
            }, 1000);
        }
        else {
            clearInterval(monitorInterval.current)
        }
    }, [location])

    const emit = (eventName, data) => {
        if (state.connected) {
            socket.current.emit(eventName, data)
        }
    };

    const record = async () => {
        if (state.connected && state.serviceSocketReady) {
            if (!state.recording) {
                setState(prv => ({ ...prv, recording: true }))
                navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true } }).then((_stream) => {
                    stream.current = _stream
                    socket.current.emit('start-microphone');
                    recorder.current = new MediaRecorder(_stream, { mimeType: "audio/webm;codecs=opus" });
                    recorder.current.ondataavailable = ({ data: blob }) => {
                        socket.current.emit('microphone-blob', blob);
                        chunks.current.push(blob)
                    }
                    const audioControl = document.getElementById('audio');
                    audioControl.setAttribute('src', "");
                    audioControl.removeAttribute('src');
                    chunks.current = []
                    recorder.current.start(500);
                    voiceDuration.current = Date.now()
                }).catch((err) => {
                    setState((prv) => ({
                        ...prv,
                        recording: false,
                    }));
                    recorder.current?.stop();
                    console.log(err);
                    socket.current.emit('stop-microphone');
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: "148",
                            variant: 'alert',
                            alert: {
                                color: "error"
                            },
                            close: true
                        })
                    )
                })
            }
            else {
                setState(prv => ({
                    ...prv,
                    lastPartial: '',
                    lastResult: '',
                    serviceSocketReady: false,
                }))
                if (window.location.pathname === '/microphone-recognition') {
                    const duration = Date.now() - voiceDuration.current
                    const audioBlob = new Blob(chunks.current, { type: 'audio/webm' })
                    const fixedBlob = await fixWebmDuration(audioBlob, duration, { logger: false })
                    const audioUrl = URL.createObjectURL(fixedBlob);
                    const audioControl = document.getElementById('audio');
                    audioControl.setAttribute('src', audioUrl);
                    audioControl.setAttribute('download', audioUrl);
                    audioControl.setAttribute('title', "voice.mp3");
                    chunks.current = []
                    voiceDuration.current = null
                }
            }
        }
    }

    const resetForm = () => {
        if (state.recording) {
            record()
        }
        setState(prv => ({
            ...prv,
            lastPartial: '',
            lastResult: ''
        }))
    }

    const clearResalt = () => {
        setState(prv => ({ ...prv, lastPartial: '', lastResult: '' }))
    }

    return (
        <SocketContext.Provider
            value={{
                ...state,
                socket,
                emit,
                record,
                resetForm,
                clearResalt
            }}
        >
            {children}
        </SocketContext.Provider>
    );
}

SocketProvider.propTypes = {
    children: PropTypes.node
};

export default SocketContext;
